/**
 * /welcome — the self-serve onboarding interview (ONBOARDING-FLOW-SPEC.md;
 * SMB-SELF-SERVE-COMPETITOR-PLAN.md Phase 1). Split screen: chat on the
 * left walks the versioned interview tree generically (S0 branch, S1
 * essential connections, S2 goal/budget/ICP/competitors/posture); the
 * right panel is the merchant's own Executive Overview assembling live,
 * reusing the real dashboard components — skeletons and real data only,
 * never a fabricated number. S3 (meet your agents) and the S4 handoff
 * follow once the interview completes.
 *
 * Resume is automatic: POST /session is get-or-create, so re-entering
 * this page always lands back on the first unanswered step with the
 * chat history (every previously-answered step) intact.
 *
 * Spec #47 §2 changes the TERMINAL, behind `FeatureFlag.PLAN_FIRST_SETUP`
 * (server-evaluated, default off — P2). Flag off, this page is byte for
 * byte the funnel that shipped, `AgentRoster` included, and no plan read
 * happens at all. Flag on, the terminal is the Plan card: the interview's
 * output IS the plan proposal (HANDOFF §12 Q28), so the last thing setup
 * asks for is an approval rather than a second, parallel way to enable
 * specialists.
 *
 * Two smaller §2 additions are unconditional because they cannot change
 * what the funnel DOES: a refusal now renders as a turn beside the live
 * question instead of a banner that displaces it, and the re-run preamble
 * tells a tenant with an approved plan what re-answering will actually
 * do before they touch the first chip.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Layout,
  Page,
  SkeletonBodyText,
  SkeletonPage,
  Text,
} from '@shopify/polaris';

import {
  createSession,
  getTree,
  submitTransition,
  type InterviewTree,
  type OnboardingSession,
  type TreeStep,
} from '../services/onboardingApi';
import { listConnectors, type ConnectorSummary } from '../services/connectorsApi';
import { setSpecialistEnabled } from '../services/marketingSuiteApi';
import { AnsweredStep, StepRenderer } from '../components/onboarding/StepRenderer';
import { ConnectStep } from '../components/onboarding/ConnectStep';
import { AgentRoster, type RosterChange } from '../components/onboarding/AgentRoster';
import { PlanTerminalCard } from '../components/onboarding/PlanTerminalCard';
import { RightPanel } from '../components/onboarding/RightPanel';
import { getPlan } from '../services/planApi';
import { isApprovedPlan } from '../types/plan';
import {
  computeTransitionId,
  resolveNext,
  stepById,
  walkPath,
} from '../components/onboarding/treeWalk';

export default function Welcome() {
  const navigate = useNavigate();
  const [tree, setTree] = useState<InterviewTree | null>(null);
  const [session, setSession] = useState<OnboardingSession | null>(null);
  const [connectors, setConnectors] = useState<ConnectorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [connectPhaseDismissed, setConnectPhaseDismissed] = useState(false);
  const [rosterFinished, setRosterFinished] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  // spec #47 P2 — a SERVER decision, read off the tree envelope. The
  // client cannot turn the new terminal on for itself.
  const [planFirst, setPlanFirst] = useState(false);
  // spec #47 §2 re-run path: a tenant who already has an approved plan is
  // told what re-answering does BEFORE the first answer, not after.
  const [hasApprovedPlan, setHasApprovedPlan] = useState(false);
  // The server's refusal for the step still on screen, rendered as a turn.
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([getTree(), createSession(), listConnectors()]).then(
      ([treeResult, sessionResult, connectorsResult]) => {
        if (cancelled) return;
        if (treeResult.status === 'rejected' || sessionResult.status === 'rejected') {
          setError('Failed to load onboarding. Refresh to try again.');
          setLoading(false);
          return;
        }
        setTree(treeResult.value.tree);
        setPlanFirst(treeResult.value.plan_first_setup === true);
        setSession(sessionResult.value.session);
        if (connectorsResult.status === 'fulfilled') {
          setConnectors(connectorsResult.value.connectors);
        } else {
          setError(
            'Could not load your connections — connection status shown here may be out of date.'
          );
        }
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshConnectors = useCallback(async () => {
    try {
      const data = await listConnectors();
      setConnectors(data.connectors);
    } catch {
      // Best-effort: the right panel simply keeps rendering from the last
      // known connector state rather than blocking on this refresh.
    }
    setRefreshToken((n) => n + 1);
  }, []);

  // The preamble read only happens on the flagged path: with the flag
  // off nothing about this page — including its network calls — differs
  // from the funnel that shipped.
  useEffect(() => {
    if (!planFirst) return;
    let cancelled = false;
    getPlan()
      .then((plan) => {
        if (!cancelled) setHasApprovedPlan(isApprovedPlan(plan));
      })
      .catch(() => {
        // No plan surface yet is not an error to show a merchant: the
        // preamble simply does not apply.
        if (!cancelled) setHasApprovedPlan(false);
      });
    return () => {
      cancelled = true;
    };
  }, [planFirst]);

  const handleAnswer = async (step: TreeStep, answer: unknown) => {
    if (!session) return;
    setSubmitting(true);
    setError(null);
    setRefusal(null);
    try {
      const transitionId = computeTransitionId(session.id, step.id, answer);
      const result = await submitTransition({
        revision: session.revision,
        step_key: step.id,
        answer,
        transition_id: transitionId,
      });
      setSession(result.session);
      setNotice(
        result.reconciled
          ? 'Your session had moved on in another tab — synced to its latest state.'
          : null
      );
    } catch (e) {
      // A refusal is the agent ANSWERING, so it belongs in the
      // conversation next to the question — which stays live, with its
      // chips, so the correction is one click rather than a reload.
      const detail =
        (e as Error & { detail?: string }).detail ||
        (e instanceof Error ? e.message : 'Could not save your answer');
      setRefusal(detail);
    } finally {
      setSubmitting(false);
    }
  };

  // The trust-ladder posture is the LAST interview step (whichever step
  // resolves to the tree's terminal step) — found generically by walking
  // the branch rather than hardcoding its id, so a tree version that
  // reorders steps needs no change here. Re-submitting it with its
  // original answer is a pure, idempotent replay server-side, and that
  // replay is what re-runs the S4 first-run dispatch now that the roster
  // reflects what the merchant actually chose to enable (the dispatch
  // that already fired when this step was first answered saw nothing
  // enabled yet — see onboarding_service.py's replay-repairs-dispatch note).
  const handleRosterFinish = async (changes: RosterChange[]) => {
    await Promise.all(changes.map((c) => setSpecialistEnabled(c.key, c.enabled)));
    if (tree && session) {
      const path = walkPath(tree, session.answers);
      const final = path[path.length - 1];
      if (final) {
        const transitionId = computeTransitionId(session.id, final.step.id, final.answer);
        const result = await submitTransition({
          revision: session.revision,
          step_key: final.step.id,
          answer: final.answer,
          transition_id: transitionId,
        });
        setSession(result.session);
      }
    }
    setRosterFinished(true);
  };

  if (loading) {
    return (
      <SkeletonPage title="Welcome">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={8} />
            </Card>
          </Layout.Section>
        </Layout>
      </SkeletonPage>
    );
  }

  if (!tree || !session) {
    return (
      <Page title="Welcome">
        <Banner tone="critical">{error ?? 'Something went wrong.'}</Banner>
      </Page>
    );
  }

  const path = walkPath(tree, session.answers);
  const currentStep = session.completed ? null : stepById(tree, session.step_key);
  const verticalStep = stepById(tree, tree.first_step);
  const postVerticalStepId =
    verticalStep && session.branch
      ? resolveNext(tree, verticalStep, session.branch)
      : null;
  const inConnectWindow =
    !!postVerticalStepId &&
    session.step_key === postVerticalStepId &&
    !connectPhaseDismissed;

  const goal = typeof session.answers.goal === 'string' ? session.answers.goal : null;
  const hasBudget =
    Array.isArray(session.answers.budget) && session.answers.budget.length > 0;
  const competitors = Array.isArray(session.answers.competitors)
    ? (session.answers.competitors as string[])
    : null;
  const posture =
    typeof session.answers.risk_posture === 'string'
      ? session.answers.risk_posture
      : null;
  const connectedKeys = new Set(
    connectors.filter((c) => c.is_connected).map((c) => c.connector_key)
  );

  return (
    <section className="mk-setup" aria-labelledby="setup-title">
      <p className="mk-setup-eyebrow">YOUR MARKINSIGHT WORKSPACE</p>
      <h1 id="setup-title">Let’s set up your business</h1>
      <p className="mk-setup-intro">Tell us a little about your business, connect your tools, and choose what you want to improve.</p>
      <ol className="mk-setup-stages" aria-label="Setup progress">
        {['Business', 'Connections', 'Goals', 'Review'].map((label, index) => (
          <li key={label} aria-current={index === (session.completed ? 3 : !session.branch ? 0 : inConnectWindow ? 1 : 2) ? 'step' : undefined}>
            <span aria-hidden="true">{index + 1}</span>{label}
          </li>
        ))}
      </ol>
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {notice && (
          <Banner tone="info" onDismiss={() => setNotice(null)}>
            {notice}
          </Banner>
        )}
        <div className="mk-setup-grid">
          <div className="mk-setup-card">
            <BlockStack gap="400">
              {hasApprovedPlan && (
                <Banner tone="info">
                  <Text as="p">
                    You already have an approved plan. Changing answers
                    proposes a new version — nothing changes until you
                    approve it, and settings you edited by hand are left
                    alone.
                  </Text>
                </Banner>
              )}

              {path.length > 0 && (
                <details className="mk-setup-history">
                  <summary>Your saved answers ({path.length})</summary>
                  <BlockStack gap="300">
                    {path.map((entry) => (
                      <AnsweredStep key={entry.step.id} step={entry.step} answer={entry.answer} />
                    ))}
                  </BlockStack>
                </details>
              )}

              {inConnectWindow && session.branch && (
                <ConnectStep
                  branch={session.branch}
                  connectors={connectors}
                  onConnected={() => void refreshConnectors()}
                  onContinue={() => setConnectPhaseDismissed(true)}
                />
              )}

              {!inConnectWindow && currentStep && (
                <StepRenderer
                  key={currentStep.id}
                  step={currentStep}
                  submitting={submitting}
                  refusal={refusal}
                  onSubmit={(answer) => void handleAnswer(currentStep, answer)}
                />
              )}

              {/* The flagged terminal. `AgentRoster` is retired from THIS
                  path only — enabling a specialist becomes a consequence
                  of approving the plan, which is the one write path. */}
              {session.completed && planFirst && (
                <PlanTerminalCard
                  onOpenWorkspace={() => navigate('/marketing-agents')}
                />
              )}

              {session.completed && !planFirst && !rosterFinished && (
                <AgentRoster
                  connectedKeys={connectedKeys}
                  posture={posture}
                  onFinish={handleRosterFinish}
                />
              )}

              {session.completed && !planFirst && rosterFinished && (
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">
                    You&apos;re all set.
                  </Text>
                  <Text as="p">
                    Your preferences are saved. Open your overview to check your
                    connections and next steps. Recommendations need connected data
                    and the required plan and AI settings.
                  </Text>
                  <InlineStack>
                    <Button variant="primary" onClick={() => navigate('/dashboards')}>
                      Go to your dashboard
                    </Button>
                  </InlineStack>
                </BlockStack>
              )}
            </BlockStack>
          </div>
          <aside className="mk-setup-summary" aria-label="Your setup summary">
            <h2>{session.branch ? 'Your business at a glance' : 'A clear path to your first insights'}</h2>
            {!session.branch && <p>Start with your business type. We’ll suggest the connections that fit, then help you set a clear marketing goal.</p>}
            <dl>
              <dt>Business type</dt>
              <dd>{session.branch ? ({shopify_merchant: 'Shopify store', b2b_saas: 'B2B software business', agency: 'Marketing agency'}[session.branch] ?? session.branch.replaceAll('_', ' ')) : 'Not selected yet'}</dd>
              <dt>Connected tools</dt>
              <dd>{connectedKeys.size ? `${connectedKeys.size} connected` : 'None connected yet'}</dd>
              <dt>Your goal</dt>
              <dd>{goal ? goal.replaceAll('_', ' ') : 'We’ll choose this together'}</dd>
            </dl>
            <footer>Answers are saved as you go. You can return to setup from the sidebar.</footer>
          </aside>
        </div>
        {session.branch && (
          <details className="mk-setup-preview">
            <summary>Preview your overview</summary>
            <RightPanel goal={goal} hasBudget={hasBudget} competitors={competitors} refreshToken={refreshToken} />
          </details>
        )}
      </BlockStack>
    </section>
  );
}
